import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { 
  Settings2, Shield, DollarSign, Cpu, Leaf, PiggyBank,
  GripVertical, Eye, EyeOff, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'cartie_widget_preferences';

// Default widget configurations
const DEFAULT_WIDGETS = {
  roleWidget: { enabled: true, order: 0 },
  esgWidget: { enabled: true, order: 1 },
  savingsWidget: { enabled: true, order: 2 },
  costTrend: { enabled: true, order: 3 },
  serviceBreakdown: { enabled: true, order: 4 },
  anomalies: { enabled: true, order: 5 },
  recommendations: { enabled: true, order: 6 },
};

const WidgetSettings = ({ onSettingsChange }) => {
  const { user, isAdmin, isFinance, isEngineering, canAccess } = useAuth();
  const [open, setOpen] = useState(false);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [hasChanges, setHasChanges] = useState(false);

  // Load saved preferences on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setWidgets({ ...DEFAULT_WIDGETS, ...parsed });
      } catch (e) {
        console.error('Failed to parse widget preferences');
      }
    }
  }, []);

  // Widget metadata with permission requirements
  const widgetMeta = {
    roleWidget: {
      name: isAdmin ? 'Admin Control Center' : isFinance ? 'Finance Dashboard' : 'Engineering Hub',
      description: 'Role-specific quick actions and shortcuts',
      icon: isAdmin ? Shield : isFinance ? DollarSign : Cpu,
      color: isAdmin ? 'rose' : isFinance ? 'sky' : 'emerald',
      permission: null, // Always accessible
    },
    esgWidget: {
      name: 'Sustainability (ESG)',
      description: 'Carbon footprint and ESG score',
      icon: Leaf,
      color: 'green',
      permission: 'cloud_pl', // Only Admin/Finance
    },
    savingsWidget: {
      name: 'Savings Tracker',
      description: 'Implementation progress and opportunities',
      icon: PiggyBank,
      color: 'amber',
      permission: null, // Always accessible
    },
    costTrend: {
      name: 'Cost Trend & Forecast',
      description: 'Historical costs with AI predictions',
      icon: DollarSign,
      color: 'emerald',
      permission: null,
    },
    serviceBreakdown: {
      name: 'Cost by Service',
      description: 'Service-level cost breakdown',
      icon: Cpu,
      color: 'blue',
      permission: null,
    },
    anomalies: {
      name: 'Active Anomalies',
      description: 'Cost spikes requiring attention',
      icon: Shield,
      color: 'amber',
      permission: null,
    },
    recommendations: {
      name: 'Optimization Recommendations',
      description: 'AI-powered savings suggestions',
      icon: PiggyBank,
      color: 'emerald',
      permission: null,
    },
  };

  // Check if widget is accessible based on permissions
  const canAccessWidget = (widgetId) => {
    const meta = widgetMeta[widgetId];
    if (!meta || !meta.permission) return true;
    return canAccess(meta.permission);
  };

  // Get accessible widgets
  const accessibleWidgets = Object.keys(widgetMeta).filter(canAccessWidget);

  const handleToggle = (widgetId) => {
    setWidgets(prev => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], enabled: !prev[widgetId].enabled }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
    if (onSettingsChange) {
      onSettingsChange(widgets);
    }
    setHasChanges(false);
    toast.success('Widget preferences saved');
    setOpen(false);
  };

  const handleReset = () => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.removeItem(STORAGE_KEY);
    if (onSettingsChange) {
      onSettingsChange(DEFAULT_WIDGETS);
    }
    setHasChanges(false);
    toast.success('Widget preferences reset to defaults');
  };

  const getColorClass = (color) => {
    const colors = {
      rose: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
      sky: 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400',
      emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
      green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
      blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2"
          data-testid="widget-settings-btn"
        >
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">Customize</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="widget-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-emerald-600" />
            Dashboard Widgets
          </DialogTitle>
          <DialogDescription>
            Choose which widgets appear on your dashboard. Some widgets may be restricted based on your role.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[400px] overflow-y-auto py-2">
          {accessibleWidgets.map((widgetId) => {
            const meta = widgetMeta[widgetId];
            const widget = widgets[widgetId];
            const IconComponent = meta.icon;
            
            return (
              <div 
                key={widgetId}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  widget.enabled 
                    ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800' 
                    : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 opacity-60'
                }`}
                data-testid={`widget-toggle-${widgetId}`}
              >
                <div className={`p-2 rounded-lg ${getColorClass(meta.color)}`}>
                  <IconComponent className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                    {meta.name}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {meta.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {widget.enabled ? (
                    <Eye className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  <Switch
                    checked={widget.enabled}
                    onCheckedChange={() => handleToggle(widgetId)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {!canAccess('cloud_pl') && (
          <div className="text-xs text-slate-400 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg">
            <span className="font-medium">Note:</span> Some widgets like ESG are only available to Admin and Finance roles.
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleReset}
            className="gap-1 text-slate-400"
          >
            <RotateCcw className="w-3 h-3" />
            Reset to Default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WidgetSettings;

// Export helper to get widget preferences
export const getWidgetPreferences = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      return { ...DEFAULT_WIDGETS, ...JSON.parse(saved) };
    } catch (e) {
      return DEFAULT_WIDGETS;
    }
  }
  return DEFAULT_WIDGETS;
};
